import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

import { LISTING_COLUMNS } from "@/lib/property-columns";

/**
 * Slim properties query for public listing surfaces (PropertyCard, Wishlist).
 * Fetches only the columns rendered on cards — avoids pulling 40+ admin fields
 * on every page load. Column list lives in `@/lib/property-columns` so it
 * stays in sync with other public-safe reads.
 *
 * For admin views that need the full row, use `useProperties()` instead.
 */



export const PROPERTY_PAGE_SIZE = 6;

export interface PropertyListing {
  id: string;
  title: string;
  slug: string;
  image_url: string;
  image: string;
  tagline: string;
  description: string;
  price_per_night: number;
  price_per_night_low: number | null;
  cleaning_fee: number | null;
  pet_fee: number | null;
  pets_allowed: boolean | null;
  bedrooms: number;
  beds: number;
  bathrooms: number;
  sleeps: number;
  amenities: string[];
  unit_number: string | null;
  street_address: string | null;
  internal_listing_name: string | null;
  min_nights: number | null;
  created_at: string;
}

const mapRow = (p: any): PropertyListing => ({
  ...p,
  image: p.image_url,
});

interface InfiniteListingsOptions {
  pageSize?: number;
  /**
   * Optional whitelist of property ids to constrain results to (e.g. availability
   * filter from `check_availability` RPC). When provided, server-side pagination
   * still applies — only the matching subset is paged.
   * Pass `null`/`undefined` to disable the filter; pass `[]` to short-circuit
   * to an empty result without hitting the network.
   */
  availableIds?: string[] | null;
  enabled?: boolean;
}

/**
 * Cursor-paginated infinite listings keyed off `created_at`.
 * Loads `pageSize` rows per page; `fetchNextPage()` advances the cursor.
 * When `availableIds` is supplied, results are constrained server-side via `.in()`.
 */
export const usePropertyListingsInfinite = ({
  pageSize = PROPERTY_PAGE_SIZE,
  availableIds,
  enabled = true,
}: InfiniteListingsOptions = {}) => {
  // Stable cache key for the id whitelist (sort to avoid order-driven cache misses)
  const idsKey = availableIds ? [...availableIds].sort().join(",") : null;

  return useInfiniteQuery({
    queryKey: ["property-listings-infinite", pageSize, idsKey],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }): Promise<PropertyListing[]> => {
      // Empty whitelist → no possible matches
      if (availableIds && availableIds.length === 0) return [];

      let query = supabase
        .from("properties")
        .select(LISTING_COLUMNS)
        .eq("is_active", true)
        .order("created_at", { ascending: true })
        .order("id", { ascending: true })
        .limit(pageSize);

      if (availableIds && availableIds.length > 0) {
        query = query.in("id", availableIds);
      }

      if (pageParam) {
        query = query.gt("created_at", pageParam);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []).map(mapRow);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length < pageSize) return undefined;
      return lastPage[lastPage.length - 1].created_at;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Non-paginated variant retained for surfaces that need the full set
 * (e.g., Wishlist filters by an arbitrary id list).
 */
export const usePropertyListings = () => {
  return useQuery({
    queryKey: ["property-listings"],
    queryFn: async (): Promise<PropertyListing[]> => {
      const { data, error } = await supabase
        .from("properties")
        .select(LISTING_COLUMNS)
        .eq("is_active", true)
        .order("created_at");

      if (error) throw error;
      return (data ?? []).map(mapRow);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
